import privateAxiosInstance from "../utils/axios/private";
import { hybridEncrypt, hybridDecrypt } from "../utils/crypto";

export const jdApi = {
    /**
     * Fetch JD list for Job Seeker (GET)
     * Endpoint: /jd/jobSeeker/
     */
    async getJobSeekerJDs({ page = 1, page_size = 10 } = {}) {
        try {
            const response = await privateAxiosInstance.get("/jd/jobSeeker/", {
                params: { page, page_size },
            });
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                };
            }
            return error.response?.data;
        }
    },

    /**
     * Fetch all JDs for Recruiter (GET)
     * Endpoint: /jd/recruiter/
     */
    async getRecruiterJDs({ page = 1, page_size = 10, pagination = "true" } = {}) {
        try {
            const response = await privateAxiosInstance.get("/jd/recruiter/", {
                params: { page, page_size, pagination }
            });
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Create new JD for Recruiter (POST)
     * Endpoint: /jd/recruiter/
     * @param {object} payload - JD creation data
     */
    async createRecruiterJD(payload) {
        try {
            const response = await privateAxiosInstance.post("/jd/recruiter/", payload);

            const finalData = response.data.encrypted
                ? await hybridDecrypt(response.data)
                : response.data;

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };

        } catch (error) {

            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "NETWORK_ERROR",
                    success: false,
                };
            }

            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },

    /**
     * Get details of a specific JD (GET)
     * Endpoint: /jd/recruiter/:pk/
     * @param {string} pk - JD UUID
     */
    async getRecruiterJDDetail(pk) {
        try {
            const response = await privateAxiosInstance.get(`/jd/recruiter/${pk}/`);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    async updateRecruiterJDDetail(pk, payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload)
            const response = await privateAxiosInstance.put(`/jd/recruiter/${pk}/`, encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    async getJDMatchFilter({payload, page = 1, page_size = 5}) {
        try {
            const encryptedPayload = await hybridEncrypt(payload)
            const response = await privateAxiosInstance.post('/jd/match/filter/', encryptedPayload, { params: { page, page_size },});
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    message: "Network error",
                    type: "network_error",
                };
            }
            return error.response?.data;
        }
    },

    /**
     * Delete a JD (DELETE)
     * Endpoint: /jd/recruiter/:pk/
     * @param {string} pk - JD UUID
     */
    async deleteRecruiterJD(pk) {
        try {
            const response = await privateAxiosInstance.delete(`/jd/recruiter/${pk}/`);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Fetch vectorized details of a JD (GET)
     * Endpoint: /jd/vector/:pk/
     * @param {string} pk - JD UUID
     */
    async getJDVectorDetail(pk) {
        try {
            const response = await privateAxiosInstance.get(`/jd/vector/${pk}/`);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    async viewJD(payload) {
        try {
            const response = await privateAxiosInstance.get(`/jd/view/`, { params: payload });
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },


    async getJDMatchResults({jdId, page = 1, page_size = 5}) {
        try {
            const response = await privateAxiosInstance.get(`/jd/match/${jdId}/`, {params: {page, page_size,},});
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    message: "Network error",
                    type: "network_error",
                };
            }
            return error.response?.data;
        }
    },
    async FetchResumeFromMail(payload) {
        try {
            const response = await privateAxiosInstance.post("/jd/fetchresume/mail/", payload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    async statusUpdate(pk, payload) {
        try {
             const encryptedPayload = await hybridEncrypt(payload)
            const response = await privateAxiosInstance.put(`/jd/status/update/${pk}/`, encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    message: "Network error",
                    type: "network_error",
                };
            }
            return error.response?.data;
        }
    },
};
